
import Vue, { PropType } from "vue";
import { getBrandConfig } from "@/config/brands";
import { Category } from "@/config/types";
import { SubFilterOption } from "@/config/subFilters";

interface GameCategory {
  name: string;
  active: boolean;
  comingSoon: boolean;
  disabled: boolean;
}

export default Vue.extend({
  name: "SideBar",
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    selectedCategory: {
      type: String,
      default: null,
    },
    currentBrandId: {
      type: String,
      required: true,
    },
    subFilters: {
      type: Array as PropType<SubFilterOption[]>,
      default: () => [],
    },
    activeBrandFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    subFilterableCategories: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    activeIntegration: {
      type: String as PropType<string | null>,
      default: null,
    },
  },
  data() {
    return {
      gameCategories: [] as GameCategory[],
      brandsOpen: false,
      integrationsOpen: false,
      managementOpen: false,
    };
  },
  computed: {
    isAdminBrand(): boolean {
      return this.currentBrandId === 'jogoglobal-admin';
    },
    brandConfig() {
      return getBrandConfig(this.currentBrandId);
    },
    primaryColor(): string {
      return this.brandConfig.primaryColor;
    },
    logoPath(): string {
      try {
        return require(`@/assets/${this.brandConfig.logo}`);
      } catch (e) {
        console.warn(`Logo not found: ${this.brandConfig.logo}`);
        return require("@/assets/BetMGM.svg");
      }
    },
  },
  watch: {
    currentBrandId: {
      immediate: true,
      handler() {
        this.loadCategories();
      },
    },
    activeIntegration: {
      immediate: true,
      handler(val: string | null) {
        if (val) this.integrationsOpen = true;
      },
    },
    selectedCategory: {
      immediate: true,
      handler(newCategory) {
        if (newCategory && this.isAdminBrand) {
          this.brandsOpen = true;
        }
        this.gameCategories.forEach((g) => (g.active = false));
        if (newCategory) {
          const category = this.gameCategories.find(
            (g) => g.name === newCategory
          );
          if (category) {
            category.active = true;
          }
        }
      },
    },
  },
  methods: {
    loadCategories() {
      const config = getBrandConfig(this.currentBrandId);
      this.gameCategories = config.categories.map((cat: Category) => ({
        name: cat.name,
        active: false,
        comingSoon: cat.comingSoon || false,
        disabled: cat.disabled || false,
      }));
    },

    selectGame(game: GameCategory) {
      if (game.disabled) {
        return;
      }

      if (game.active) {
        game.active = false;
        this.$emit("game-selected", null);
      } else {
        this.gameCategories.forEach((g) => (g.active = false));
        game.active = true;
        this.$emit("game-selected", game.name);
      }

      if (this.isMobile) {
        const hasSubFilters = this.subFilterableCategories.includes(game.name);
        if (!hasSubFilters) this.$emit("close-mobile");
      }
    },

    onSubFilterClick(sf: SubFilterOption) {
      if (sf.comingSoon) return;
      const next = sf.isAll
        ? null
        : this.activeBrandFilter === sf.value
        ? null
        : sf.value;
      this.$emit("brand-filter-changed", next);
      if (this.isMobile) this.$emit("close-mobile");
    },

    onIntegrationClick(integration: string) {
      const next = this.activeIntegration === integration ? null : integration;
      this.$emit('integration-filter-changed', next);
      if (this.isMobile) this.$emit('close-mobile');
    },

    resetToHome() {
      this.gameCategories.forEach((g) => (g.active = false));

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      this.$emit("game-selected", null);
      this.$emit("reset-selection");

      if (this.isMobile) {
        this.$emit("close-mobile");
      }
    },
  },

  mounted() {
    this.loadCategories();
  },
});
